import { Box, Divider, Grid, Link, Stack, Typography } from '@mui/material';
import { backendServices, Button, Card, SimpleMarkdownRenderer } from 'common';

import { GuideIcon } from './GuideIcon';

function getColumnAlignment({ logosLength, numberOfColumns }: { logosLength: number; numberOfColumns: number }) {
    if (numberOfColumns === 2) {
        return 'center';
    }

    if (logosLength === 0) {
        return 'center';
    }

    return 'flex-start';
}

export function SubmissionStep({
    step,
}: {
    step: backendServices.ViewModels.SubmissionGuideLayoutViewModel['steps'][number];
}) {
    const numberOfColumns = step.instructions.length;

    return (
        <Card
            rootProps={{
                'data-testid': `submission-step-${step.id}`,
            }}
            headerProps={{
                title: step.title,
                titleTypographyProps: { variant: 'subtitle1', 'data-testid': 'card-title' },
                subheader: <Divider sx={{ mt: 1 }} />,
            }}
            content={
                <Grid container spacing={2}>
                    {step.instructions.map((column, idx) => {
                        const buttons = column.urls.filter(({ buttonTitle }) => buttonTitle != null);
                        const links = column.urls.filter(({ buttonTitle }) => buttonTitle == null);

                        return (
                            <>
                                <Grid
                                    key={(column.subtitle ?? '').concat(column.text ?? '').concat(idx.toString())}
                                    item
                                    xs={12}
                                    sm={numberOfColumns === 2 ? 5 : 12}
                                    data-testid="instruction-column"
                                >
                                    <Stack
                                        direction={numberOfColumns === 2 ? 'column' : 'row'}
                                        spacing={4}
                                        px={2}
                                        alignItems="center"
                                    >
                                        <Stack
                                            spacing={2}
                                            width="100%"
                                            alignItems={getColumnAlignment({
                                                logosLength: column.logos.length,
                                                numberOfColumns,
                                            })}
                                        >
                                            {!!column.subtitle && (
                                                <Typography
                                                    variant="subtitle2"
                                                    whiteSpace="pre-wrap"
                                                    textAlign="center"
                                                    data-testid="column-subtitle"
                                                >
                                                    {column.subtitle}
                                                </Typography>
                                            )}
                                            {!!column.text && <SimpleMarkdownRenderer markdown={column.text} />}
                                            {!!links.length && (
                                                <Stack spacing={2}>
                                                    {links.map(({ url: emailAddress }, linkIdx) => (
                                                        <Link
                                                            variant="subtitle1"
                                                            href={`mailto:${emailAddress}`}
                                                            underline="none"
                                                            sx={{ overflowWrap: 'break-word' }}
                                                            data-testid={`column-link-${linkIdx}`}
                                                        >
                                                            {emailAddress}
                                                        </Link>
                                                    ))}
                                                </Stack>
                                            )}
                                            {!!buttons.length && (
                                                <Stack
                                                    direction={
                                                        numberOfColumns === 2
                                                            ? 'column'
                                                            : {
                                                                  xs: 'column',
                                                                  md: 'row',
                                                              }
                                                    }
                                                    flexWrap={{ md: 'wrap' }}
                                                    spacing={2}
                                                >
                                                    {buttons.map(({ buttonTitle, url }, buttonIdx) => {
                                                        const fullURL = new URL(
                                                            `/SubmissionDocuments/${url}`,
                                                            window.location.origin
                                                        );

                                                        return (
                                                            <Button
                                                                key={url}
                                                                onClick={() => {
                                                                    window.open(fullURL, '_blank', 'noopener');
                                                                }}
                                                                testid={`column-button-${buttonIdx}`}
                                                            >
                                                                {buttonTitle}
                                                            </Button>
                                                        );
                                                    })}
                                                </Stack>
                                            )}
                                        </Stack>
                                        <Stack direction="row" spacing={2}>
                                            {column.logos.map((logo) => (
                                                <Box key={logo} width="87px" display="flex" justifyContent="center">
                                                    <GuideIcon logo={logo} />
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Stack>
                                </Grid>
                                {numberOfColumns === 2 && idx === 0 && (
                                    <Grid item xs={2} data-testid="divider-container">
                                        <Stack direction="row" height="100%" justifyContent="center">
                                            <Divider orientation="vertical" />
                                        </Stack>
                                    </Grid>
                                )}
                            </>
                        );
                    })}
                </Grid>
            }
        />
    );
}
